import { recordMobileWebRuntime } from '../IntersectEvent/mobileWebDiagnostics';
import type { GraphicsQuality, UnityGraphicsQuality } from './types';

export const GRAPHICS_QUALITY_STORAGE_KEY = 'unity-graphics-quality';

export const GRAPHICS_QUALITY_LABELS: Record<GraphicsQuality, string> = {
  auto: 'Auto',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  performance: 'Very low'
};

const UNITY_QUALITY_LEVELS: Record<Exclude<GraphicsQuality, 'auto'>, UnityGraphicsQuality> = {
  high: 'Web_High',
  medium: 'Web_Medium',
  low: 'Web_Low',
  performance: 'Web_VeryLow'
};

export function getStoredGraphicsQuality(): GraphicsQuality {
  try {
    const stored = localStorage.getItem(GRAPHICS_QUALITY_STORAGE_KEY);
    if (stored === 'auto' || stored === 'high' || stored === 'medium' || stored === 'low' || stored === 'performance') {
      return stored;
    }
  } catch (_) {}
  return 'auto';
}

/** Match the conservative browser capability heuristic used by vv-web-app. */
export function recommendGraphicsQuality(): Exclude<GraphicsQuality, 'auto'> {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const memory = nav.deviceMemory ?? 0;
  const cores = nav.hardwareConcurrency ?? 0;
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(nav.userAgent);
  const screenPixels = window.screen.width * window.screen.height * Math.max(1, window.devicePixelRatio || 1) ** 2;

  if (!memory && !cores) return 'performance';
  if ((memory > 0 && memory <= 2) || (cores > 0 && cores <= 2)) return 'performance';
  if (mobile) return memory >= 6 && cores >= 6 ? 'low' : 'performance';
  if ((memory > 0 && memory <= 4) || (cores > 0 && cores <= 4) || screenPixels > 14_000_000) return 'low';
  if ((memory >= 8 || memory === 0) && cores >= 8 && screenPixels <= 9_000_000) return 'high';
  return 'medium';
}

export function resolveUnityGraphicsQuality(preference: GraphicsQuality, mobilePilot = false): UnityGraphicsQuality {
  const resolved = preference === 'auto' ? (mobilePilot ? 'performance' : recommendGraphicsQuality()) : preference;
  return UNITY_QUALITY_LEVELS[resolved];
}

/** Auto boots safely, then promotes after Unity reports that the world is ready. */
export function getBootUnityGraphicsQuality(mobilePilot = false): UnityGraphicsQuality {
  // A saved desktop/high preference must not increase the mobile launch budget.
  if (mobilePilot) return 'Web_VeryLow';
  const preference = getStoredGraphicsQuality();
  return preference === 'auto' ? 'Web_VeryLow' : resolveUnityGraphicsQuality(preference);
}

/** Send an event through the BrowserBridge envelope expected by UPC. */
export function sendUnity(eventType: string, payload: unknown): boolean {
  if (!window.sendMessageToUnity) {
    console.warn(`[Unity] sendUnity('${eventType}') skipped: Unity not loaded`);
    return false;
  }

  try {
    window.sendMessageToUnity(
      'BrowserBridge',
      'ReceivePayload',
      JSON.stringify({ eventType, payload: JSON.stringify(payload ?? {}) })
    );
    return true;
  } catch (error) {
    console.warn(`[Unity] sendUnity('${eventType}') failed:`, error);
    return false;
  }
}

/** Persist the browser preference and switch the running UPC build immediately. */
export function setUnityGraphicsQuality(preference: GraphicsQuality, mobilePilot = false): boolean {
  try {
    localStorage.setItem(GRAPHICS_QUALITY_STORAGE_KEY, preference);
  } catch (_) {}
  const qualityLevel = resolveUnityGraphicsQuality(preference, mobilePilot);
  const sent = sendUnity('SetGraphicsQuality', { qualityLevel });
  if (sent) recordMobileWebRuntime({ quality: qualityLevel });
  return sent;
}

export function applyAutomaticGraphicsQuality(mobilePilot = false): Exclude<GraphicsQuality, 'auto'> | null {
  if (!mobilePilot && getStoredGraphicsQuality() !== 'auto') return null;
  const recommended = mobilePilot ? 'performance' : recommendGraphicsQuality();
  const qualityLevel = resolveUnityGraphicsQuality(recommended);
  if (sendUnity('SetGraphicsQuality', { qualityLevel })) recordMobileWebRuntime({ quality: qualityLevel });
  return recommended;
}
